import { ChangeDetectionStrategy, Component, OnInit, Signal, WritableSignal, computed, signal } from '@angular/core';
import { DietApi } from 'api/diet/diet-api.js';
import { UserApi } from 'api/user/user-api.js';
import { WorkoutApi } from 'api/workout/workout-api.js';
import { DietDb } from 'lib/cosmos/diet/dietDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { WorkoutDb } from 'lib/cosmos/workout/workoutDb.js';
import { Observable, map } from 'rxjs';
import { CoreAppComponent } from 'src/app/core/core-app.component.js';
import { IViewOrEditDietPlanRouteData } from 'src/app/routes/trainer/diet-routes.js';
import { IViewOrEditWorkoutPlanRouteData } from 'src/app/routes/trainer/workout-routes.js';
import { ISharedData } from '../type-routes/type-route.models.js';

type SupportedPdfType = 'workout' | 'diet' | 'trainingPlan' | 'dietPlan';

interface IPdfRequestData {
	type: SupportedPdfType;
	itemId: string;
	userId: string | undefined;
	containerItemId: string | undefined;
}

@Component({
	templateUrl: './pdf-page.component.html',
	selector: 'mod-pdf-page',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class PdfPageComponent extends CoreAppComponent implements OnInit {
	public readonly pdfRequestData: WritableSignal<IPdfRequestData | undefined> = signal(undefined);

	public readonly workoutSharedData: WritableSignal<ISharedData<IViewOrEditWorkoutPlanRouteData> | undefined> =
		signal(undefined);

	public readonly dietSharedData: WritableSignal<ISharedData<IViewOrEditDietPlanRouteData> | undefined> =
		signal(undefined);

	public readonly trainingPlanSharedData: WritableSignal<ISharedData<UserDb.IUserItem> | undefined> =
		signal(undefined);

	public readonly dietPlanSharedData: WritableSignal<ISharedData<UserDb.IUserItem> | undefined> = signal(undefined);

	public readonly isValidRequest: Signal<boolean> = computed<boolean>(() => {
		if (!this.pdfRequestData()) {
			return false;
		}
		return true;
	});

	public readonly timestamp: Signal<string> = computed<string>(() => {
		return super.formatDateTime(new Date());
	});

	constructor() {
		super();

		const pdfRequestData = this.getPdfRequestData(this.dependencies.router.url);
		this.pdfRequestData.set(pdfRequestData);
	}

	ngOnInit(): void {
		const pdfRequestData = this.pdfRequestData();

		if (pdfRequestData?.type === 'workout') {
			super.subscribeToObservable(
				this.getWorkoutSharedData({
					containerItemId: pdfRequestData.containerItemId,
					userId: pdfRequestData.userId,
					workoutId: pdfRequestData.itemId
				}).pipe(
					map((response) => {
						if (response) {
							this.workoutSharedData.set(response);
						}
					})
				)
			);
		} else if (pdfRequestData?.type === 'diet') {
			super.subscribeToObservable(
				this.getDietSharedData({
					containerItemId: pdfRequestData.containerItemId,
					userId: pdfRequestData.userId,
					dietId: pdfRequestData.itemId
				}).pipe(
					map((response) => {
						if (response) {
							this.dietSharedData.set(response);
						}
					})
				)
			);
		} else if (pdfRequestData?.type === 'trainingPlan') {
			super.subscribeToObservable(
				this.getTrainingOrDietPlanSharedData({
					userId: pdfRequestData.itemId
				}).pipe(
					map((response) => {
						if (response) {
							this.trainingPlanSharedData.set(response);
						}
					})
				)
			);
		} else if (pdfRequestData?.type === 'dietPlan') {
			super.subscribeToObservable(
				this.getTrainingOrDietPlanSharedData({
					userId: pdfRequestData.itemId
				}).pipe(
					map((response) => {
						if (response) {
							this.dietPlanSharedData.set(response);
						}
					})
				)
			);
		}
	}

	private getPdfRequestData(url: string): IPdfRequestData | undefined {
		const routeSegments: string[] = url
			.split('/')
			.filter((m) => m.length)
			.map((m) => m.toLowerCase());

		if (!routeSegments.length) {
			return undefined;
		}

		const typeRaw = routeSegments?.[1];
		let type: SupportedPdfType | undefined;

		if (typeRaw === 'workout') {
			type = 'workout';
		} else if (typeRaw === 'diet') {
			type = 'diet';
		} else if (typeRaw === 'trainingPlan'.toLowerCase()) {
			type = 'trainingPlan';
		} else if (typeRaw === 'dietPlan'.toLowerCase()) {
			type = 'dietPlan';
		} else {
			return undefined;
		}

		const itemId = routeSegments?.[2];

		if (!itemId) {
			return undefined;
		}

		return {
			type: type,
			itemId: itemId,
			userId: routeSegments?.[3],
			containerItemId: routeSegments?.[4]
		};
	}

	private getTrainingOrDietPlanSharedData(data: {
		userId: string;
	}): Observable<ISharedData<UserDb.IUserItem> | undefined> {
		return this.dependencies.httpService
			.post<UserDb.IUserItem, UserApi.IGenerateTrainingOrDietPlanPdfRequest>((builder) =>
				builder
					.withCustomApiRoute('user', 'getTrainingOrDietPlanPdf')
					.withData({
						userId: data.userId
					})
					.build()
			)
			.pipe(
				map((response) => {
					const sharedData: ISharedData<UserDb.IUserItem> = {
						data: response.data,
						linkedItems: response.linkedItems
					};

					return sharedData;
				})
			);
	}

	private getWorkoutSharedData(data: {
		workoutId: string;
		userId: string | undefined;
		containerItemId: string | undefined;
	}): Observable<ISharedData<IViewOrEditWorkoutPlanRouteData> | undefined> {
		return this.dependencies.httpService
			.post<WorkoutDb.IWorkoutItem, WorkoutApi.IGenerateWorkoutPdfRequest>((builder) =>
				builder
					.withCustomApiRoute('workout', 'getWorkoutPdf')
					.withData({
						workoutId: data.workoutId,
						containerItemId: data.containerItemId,
						userId: data.userId
					})
					.build()
			)
			.pipe(
				map((response) => {
					const workoutData: ISharedData<IViewOrEditWorkoutPlanRouteData> = {
						data: {
							clientData: undefined,
							isWorkoutDesigner: false,
							workout: response.data,
							workoutType: 'workout',
							sessionForDate: undefined
						},
						linkedItems: response.linkedItems
					};

					return workoutData;
				})
			);
	}

	private getDietSharedData(data: {
		dietId: string;
		userId: string | undefined;
		containerItemId: string | undefined;
	}): Observable<ISharedData<IViewOrEditDietPlanRouteData> | undefined> {
		return this.dependencies.httpService
			.post<DietDb.IDietItem, DietApi.IGenerateDietPdfRequest>((builder) =>
				builder
					.withCustomApiRoute('diet', 'getDietPdf')
					.withData({
						dietId: data.dietId,
						containerItemId: data.containerItemId,
						userId: data.userId
					})
					.build()
			)
			.pipe(
				map((response) => {
					const workoutData: ISharedData<IViewOrEditDietPlanRouteData> = {
						data: {
							clientData: undefined,
							isDietDesigner: false,
							diet: response.data
						},
						linkedItems: response.linkedItems
					};

					return workoutData;
				})
			);
	}
}
